<template>
	<widget-settings-layout ref="layout" v-if="settings" slug="subGoal" name="Membership Goal" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-checkbox inline v-model="settings.hidePercentage">Hide percentage</b-form-checkbox>
				</b-col>
				<b-col>
					<b-form-checkbox inline v-model="settings.stripes">Animated stripes</b-form-checkbox>
				</b-col>
			</b-form-row>
			<b-form-row class="mt-3">
				<b-col>
					<b-form-group label="Goal (members)">
						<b-form-input type="number" min="0" v-model.number="settings.goal" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Current count">
						<b-form-input type="number" min="0" v-model.number="settings.count" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Title">
						<b-form-input v-model="settings.title" placeholder="Membership goal" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<text-settings :settings="settings" />
		<b-card class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Bar color">
						<color-picker v-model="settings.barColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Back color">
						<color-picker v-model="settings.backColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Text color">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Border width">
						<b-input-group append="px"><b-form-input type="number" min="0" max="100" v-model.number="settings.borderWidth" /></b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Border radius">
						<b-input-group append="px"><b-form-input type="number" min="0" max="2000" v-model.number="settings.borderRadius" /></b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Border color">
						<color-picker v-model="settings.borderColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Padding">
						<b-input-group append="px"><b-form-input type="number" min="0" max="2000" v-model.number="settings.padding" /></b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Text outline blur">
						<b-input-group append="px"><b-form-input type="number" min="0" max="100" step="any" v-model.number="settings.blurRadius" /></b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Text outline size">
						<b-input-group append="px"><b-form-input type="number" min="0" max="10" step="any" v-model.number="settings.spreadRadius" /></b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="12" lg="3">
					<b-form-group label="Outline color">
						<color-picker v-model="settings.shadowColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Layout" class="mb-3">
			<b-form-group label="Vertical align">
				<b-button-group class="d-flex">
					<b-button v-b-tooltip.hover title="Top"    :pressed="settings.verticalAlign === 'top'"    @click="settings.verticalAlign = 'top'"    variant="outline-secondary"><b-icon icon="align-top"    /></b-button>
					<b-button v-b-tooltip.hover title="Middle" :pressed="settings.verticalAlign === 'middle'" @click="settings.verticalAlign = 'middle'" variant="outline-secondary"><b-icon icon="align-middle" /></b-button>
					<b-button v-b-tooltip.hover title="Bottom" :pressed="settings.verticalAlign === 'bottom'" @click="settings.verticalAlign = 'bottom'" variant="outline-secondary"><b-icon icon="align-bottom" /></b-button>
				</b-button-group>
			</b-form-group>
		</b-card>
		<b-card title="Extra CSS" class="mb-3">
			<b-form-group><b-textarea v-model="settings.extraCSS" /></b-form-group>
		</b-card>
		<template #preview>
			<sub-goal-widget-preview :settings="settings || {}" />
		</template>
	</widget-settings-layout>
</template>

<script>
import ColorPicker from '@/components/ColorPicker.vue';
import TextSettings from '@/components/Widget/TextSettings.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import SubGoalWidgetPreview from '@/components/Widget/SubGoalWidgetPreview.vue';

export default {
	components: { ColorPicker, TextSettings, WidgetSettingsLayout, SubGoalWidgetPreview },
	beforeRouteLeave(to, from, next) {
		if(!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		loading: 0,
		saving: 0,
		saveError: null,
		settings: null,
		savedSettingsJson: null,
	}; },
	computed: {
		hasUnsavedChanges() {
			if(this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
	},
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.loading++;
			const [s, userData] = await Promise.all([
				this.$api.get('widgetSettings/sub-goal'),
				this.$api.get('userData'),
			]);
			this.subId = this.$socket.subscribe(userData?.widget_hash, {
				widgetSettingsFieldChange: msg => {
					if(msg.widgetType !== 'sub-goal') return;
					if(msg.field === 'count') this.settings.count = msg.value;
				},
			});
			this.settings = {
				goal:              parseInt(s.goal  || 0) || 0,
				count:             parseInt(s.count || 0) || 0,
				title:             s.title             || '',
				hidePercentage:    s.hidePercentage    || false,
				stripes:           s.stripes           || false,
				fontFamily:        s.fontFamily        || '',
				customFont:        s.customFont        || false,
				fontSize:          s.fontSize          || 0,
				letterSpacing:     s.letterSpacing     || 0,
				boldText:          s.boldText          || false,
				italicText:        s.italicText        || false,
				underlineText:     s.underlineText     || false,
				strikethroughText: s.strikethroughText || false,
				uppercaseText:     s.uppercaseText     || false,
				textColor:         s.textColor         || 'rgba(255,255,255,1)',
				barColor:          s.barColor          || 'rgba(0,127,255,1)',
				backColor:         s.backColor         || 'rgba(0,0,0,1)',
				borderColor:       s.borderColor       || 'rgba(0,127,255,1)',
				borderWidth:       s.borderWidth       || 0,
				borderRadius:      s.borderRadius      || 0,
				padding:           s.padding           || 0,
				blurRadius:        s.blurRadius        || 0,
				spreadRadius:      s.spreadRadius      || 0,
				shadowColor:       s.shadowColor       || 'rgba(0,0,0,1)',
				verticalAlign:     s.verticalAlign     || 'middle',
				extraCSS:          s.extraCSS          || '',
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
		} catch(e) {
			this.saveError = e;
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		_onBeforeUnload(e) {
			if(this.hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; }
		},
		async save() {
			try {
				this.saving++;
				this.saveError = null;
				await this.$api.post('widgetSettings/sub-goal', this.settings);
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(e) {
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
	},
};
</script>
